import type { ReactNode } from 'react'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  type CardProps,
} from '@/components/ui/card'
import { StyledIcon, type StyledIconProps, type StyledIconTone } from '@/components/ui/styled-icon'
import { Typography, type TypographyTone } from '@/components/ui/typography'
import LinkCard, { type LinkCardProps } from '@/components/patterns/link-card'

export type InfoCardProps = {
  action?: ReactNode
  /** Makes the whole card a link to this destination. */
  link?: Pick<LinkCardProps, 'to' | 'params' | 'search' | 'hash'>
  icon: StyledIconProps['icon']
  tone?: StyledIconTone
  title: ReactNode
  titleEnd?: ReactNode
  description?: ReactNode
  value: ReactNode
  valueTone?: TypographyTone
  accent?: CardProps['accent']
  /** Renders nothing when false. */
  condition?: boolean
}

export function InfoCard({
  action,
  link,
  icon,
  tone = 'neutral',
  title,
  titleEnd,
  description,
  value,
  valueTone,
  accent = 'none',
  condition = true,
}: InfoCardProps) {
  if (!condition) return null
  const Container = link ? LinkCard : Card

  return (
    <Container {...link} accent={accent} className="h-full justify-between gap-4">
      <CardHeader className="flex flex-row items-start gap-3 pb-3">
        <StyledIcon
          icon={icon}
          tone={tone}
          className="mt-0.5 h-8 w-8 shrink-0 p-2"
          iconClassName="h-4 w-4"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-1">
            <CardTitle className="min-w-0 text-base leading-snug">{title}</CardTitle>
            {titleEnd}
          </div>
          {description ? (
            <CardDescription className="leading-snug">{description}</CardDescription>
          ) : null}
        </div>
        {action ? <CardAction>{action}</CardAction> : null}
      </CardHeader>
      <CardContent className="mt-auto">
        <Typography variant="h3" tone={valueTone} weight="bold" className="tabular-nums">
          {value}
        </Typography>
      </CardContent>
    </Container>
  )
}
